import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  InputErrorMessage,
  Label,
  Modal,
  MultiEmailInput,
  Notification,
} from 'shared/components';
import {
  getApiErrorMessage,
  getUnsubmittedEmailError,
  normalizeEmail,
  uniqueEmails,
} from 'shared/helpers';
import { addOrderManagers } from 'orders/OrderContainer/actions';
import ManagerPermissionsPicker from '../ManagerPermissionsPicker';
import './styles.scss';

// add_managers accepts at most 20 entries per request
const CHUNK_SIZE = 20;

const BulkOrderManagersForm = ({
  orderID,
  orderManagers,
  closeCb,
  confirmCb,
}) => {
  const { t } = useTranslation();
  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [managerEmails, setManagerEmails] = useState([]);
  const [results, setResults] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const toEntry = (email) => (permissions ? { email, permissions } : { email });

  const addManagers = async (emails) => {
    const existingEmails = orderManagers.map((om) =>
      normalizeEmail(get(om, 'true_email')),
    );
    const entered = uniqueEmails(emails);
    const alreadyManagers = entered.filter((email) =>
      existingEmails.includes(normalizeEmail(email)),
    );
    const toAdd = entered.filter((email) => !alreadyManagers.includes(email));
    const added = [];
    const failed = [];

    for (let i = 0; i < toAdd.length; i += CHUNK_SIZE) {
      const chunk = toAdd.slice(i, i + CHUNK_SIZE);
      try {
        await addOrderManagers(orderID, chunk.map(toEntry));
        added.push(...chunk);
      } catch (chunkErr) {
        const chunkReason = getApiErrorMessage(chunkErr);
        if (chunk.length === 1) {
          failed.push({ email: chunk[0], reason: chunkReason });
        } else {
          // The endpoint rejects the whole list when one entry is invalid, so
          // the emails are retried one by one. Errors about an entry name its
          // email; one that doesn't (e.g. a permission the manager can't give)
          // applies to every email, so it is checked on the first email alone
          // instead of repeating it for each of them.
          const [first, ...rest] = chunk;
          try {
            await addOrderManagers(orderID, [toEntry(first)]);
            added.push(first);
          } catch (err) {
            const reason = getApiErrorMessage(err);
            if (
              reason === chunkReason &&
              !normalizeEmail(reason).includes(normalizeEmail(first))
            ) {
              return {
                added,
                alreadyManagers,
                failed,
                requestError: reason,
                notAdded: toAdd.slice(i),
              };
            }
            failed.push({ email: first, reason });
          }
          for (const email of rest) {
            try {
              await addOrderManagers(orderID, [toEntry(email)]);
              added.push(email);
            } catch (err) {
              failed.push({ email, reason: getApiErrorMessage(err) });
            }
          }
        }
      }
    }

    return { added, alreadyManagers, failed, requestError: '', notAdded: [] };
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (results) {
      return closeCb();
    }

    if (managerEmails.value) {
      setManagerEmails({
        ...managerEmails,
        validationError: getUnsubmittedEmailError(managerEmails.value),
      });
      return false;
    }

    const emails = get(managerEmails, 'emails', []);
    if (!emails.length) {
      return false;
    }

    if (Array.isArray(permissions) && !permissions.length) {
      setPermissionsError(t('Select at least one permission'));
      return false;
    }

    setFormError('');
    setLoading(true);
    const outcome = await addManagers(emails);
    setLoading(false);

    // Nothing was added, so the form stays as it was for another try
    if (outcome.requestError && !outcome.added.length) {
      setFormError(outcome.requestError);
      return false;
    }

    if (outcome.added.length) {
      confirmCb();
    }

    if (
      !outcome.alreadyManagers.length &&
      !outcome.failed.length &&
      !outcome.notAdded.length
    ) {
      Notification(
        'success',
        t('Changes saved successfully'),
        t('License managers have been added'),
      );
      return closeCb();
    }

    setResults(outcome);
    return true;
  };

  const handleEmailSubmit = (val) => {
    setDirty(true);
    setManagerEmails(val);
  };

  const handleClose = () => {
    if (!dirty || results) {
      return closeCb();
    }
    return setDirtyFormAlertDisplay(true);
  };

  // A note is a reason shared by every email in the group, shown once
  const renderResultGroup = (title, items, className, note = '') =>
    items.length > 0 && (
      <div className={`BulkOrderManagersForm-result ${className}`}>
        <div className="result-title">{`${title} (${items.length})`}</div>
        {note && <div className="result-reason">{note}</div>}
        <ul>
          {items.map(({ email, reason }) => (
            <li key={email}>
              {email}
              {reason && <span className="result-reason">{reason}</span>}
            </li>
          ))}
        </ul>
      </div>
    );

  const toItems = (emails) => emails.map((email) => ({ email }));

  return (
    <Modal
      title={t('Add managers in bulk')}
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={loading || permissionsLoading}
      size="lg"
    >
      <div className="BulkOrderManagersForm">
        {results ? (
          <div className="BulkOrderManagersForm-results">
            {renderResultGroup(t('Added'), toItems(results.added), 'is-added')}
            {renderResultGroup(
              t('Already a license manager'),
              toItems(results.alreadyManagers),
              'is-skipped',
            )}
            {renderResultGroup(
              t('Could not be added'),
              results.failed,
              'is-failed',
            )}
            {renderResultGroup(
              t('Could not be added'),
              toItems(results.notAdded),
              'is-failed',
              results.requestError,
            )}
          </div>
        ) : (
          <div>
            <Label text={t('User emails')} inputId="emails-input" />
            <MultiEmailInput
              emails={get(managerEmails, 'emails')}
              onEmailSubmit={handleEmailSubmit}
            />
            <InputErrorMessage
              text={get(managerEmails, 'validationError', '')}
            />
            <ManagerPermissionsPicker
              permissions={permissions}
              onChange={(val) => {
                setDirty(true);
                setPermissions(val);
                setPermissionsError('');
              }}
              onLoadingChange={setPermissionsLoading}
              error={permissionsError}
              disabled={loading}
            />
            <InputErrorMessage text={formError} />
          </div>
        )}
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormAlertDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

BulkOrderManagersForm.propTypes = {
  orderID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  orderManagers: PropTypes.array.isRequired,
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default BulkOrderManagersForm;
